import { PhoneIcon, CurrencyDollarIcon, MapPinIcon } from '@heroicons/react/24/solid';
import ProfileFormHeader from '../../ProfileFormHeader/ProfileFormHeader';
import FullRow from '../../../../../components/formElements/FullRow';
import HalfRow from '../../../../../components/formElements/HalfRow';
import FormInputField from '../../../../../components/formElements/FormInputField';
import FormTextArea from '../../../../../components/formElements/FormTextArea';
import FormSelectDrop from '../../../../../components/formElements/FormSelectDrop';
import FormButton from '../../../../../components/formElements/FormButton';
import CustomRedAlert from '../../../../../components/alerts/customRedAlert/CustomRedAlert';
import CustomSuccessModal from '../../../../../components/alerts/customSuccessModal/CustomSuccessModal';
import useInitialForm from './hooks/useInitialForm';
import FormFieldset from '../../../../../components/formElements/FormFieldset';
import FormCheckBox from '../../../../../components/formElements/FormCheckBox';
import { useEffect, useRef, useState } from 'react';
import WhiteCardContainer from '../../../../../components/containers/forms/WhiteCardContainer';
import FormFieldsContainer from '../../../../../components/containers/forms/FormFieldsContainer';
import FormFieldsFooter from '../../../../../components/containers/forms/FormFieldsFooter';
import { useFieldArray, Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

export default function InitialProfileForm(props) {
    const {
        cities,
        basicProfile,
        specialities,
        queryBasicProfile,
        selectedUser,
        fetchData,
        querySecondarySpecialitiesByPrimaryId
    } = props;

    const {
        showErrorModal,
        closeErrorMessage,
        showSuccessModal,
        successBody,
        errorBody,
        register,
        handleSubmit,
        errors,
        watch,
        setValue,
        formattedCities,
        formattedSpecialities,
        // isLoading,
        onCloseSuccessMessage,
        saveProfileForm,
        isEditing,
        defaultValues,
        control
    } = useInitialForm(basicProfile, cities, specialities, queryBasicProfile, selectedUser, fetchData);

    // State to store country codes
    const [phone1CountryCode, setPhone1CountryCode] = useState('');
    const [phone2CountryCode, setPhone2CountryCode] = useState('');

    // Track if we've already fetched secondary specialties for the current primary specialty
    const [lastFetchedPrimarySpecialty, setLastFetchedPrimarySpecialty] = useState(null);
    // Track if user has manually changed the primary specialty to prevent initial value effect from running
    const [hasManualChange, setHasManualChange] = useState(false);
    // Track the current selected primary specialty to preserve it when switching tabs
    const [currentSelectedPrimarySpecialty, setCurrentSelectedPrimarySpecialty] = useState(null);

    // Manual handler for primary specialty change
    const handlePrimarySpecialtyChange = (event) => {
        const raw = event.target.value;
        const newPrimarySpecialtyId = raw === '' ? '' : String(raw);

        if (!querySecondarySpecialitiesByPrimaryId) return;

        if (!newPrimarySpecialtyId) {
            setLastFetchedPrimarySpecialty(null);
            setCurrentSelectedPrimarySpecialty('');
            querySecondarySpecialitiesByPrimaryId(null);
            return;
        }

        const last = lastFetchedPrimarySpecialty == null ? null : String(lastFetchedPrimarySpecialty);
        if (newPrimarySpecialtyId !== last) {
            setLastFetchedPrimarySpecialty(newPrimarySpecialtyId);
            setHasManualChange(true);
            setCurrentSelectedPrimarySpecialty(newPrimarySpecialtyId);
            querySecondarySpecialitiesByPrimaryId(newPrimarySpecialtyId);
        }
    };

    // Initialize current selected primary specialty and fetch secondary specialties when form is first initialized
    useEffect(() => {
        if (
            defaultValues &&
            Object.keys(defaultValues).length > 0 &&
            defaultValues.idSpecialty &&
            querySecondarySpecialitiesByPrimaryId &&
            String(defaultValues.idSpecialty) !==
                (lastFetchedPrimarySpecialty == null ? null : String(lastFetchedPrimarySpecialty)) &&
            !hasManualChange
        ) {
            const id = String(defaultValues.idSpecialty);
            setLastFetchedPrimarySpecialty(id);
            setCurrentSelectedPrimarySpecialty(id);
            querySecondarySpecialitiesByPrimaryId(id);
        }
    }, [defaultValues, querySecondarySpecialitiesByPrimaryId, lastFetchedPrimarySpecialty, hasManualChange]);

    useEffect(() => {
        if (defaultValues && Object.keys(defaultValues).length > 0) {
            if (defaultValues.patientContactPhone1CountryCode) {
                setPhone1CountryCode(defaultValues.patientContactPhone1CountryCode);
            }
            if (defaultValues.patientContactPhone2CountryCode) {
                setPhone2CountryCode(defaultValues.patientContactPhone2CountryCode);
            }
        }
    }, [defaultValues]);

    // Reset the tracking state when selectedUser changes (switching between different doctors)
    useEffect(() => {
        setLastFetchedPrimarySpecialty(null);
        setHasManualChange(false); // Reset manual change flag when switching users
        setCurrentSelectedPrimarySpecialty(null); // Reset current selection when switching users
    }, [selectedUser.id]);

    // Set form value when current selection changes (to preserve selection when switching tabs)
    useEffect(() => {
        if (currentSelectedPrimarySpecialty && setValue) {
            setValue('idSpecialty', currentSelectedPrimarySpecialty, { shouldValidate: true });
        }
    }, [currentSelectedPrimarySpecialty, setValue]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'addresses'
    });

    const transfersRef = useRef(false);
    const remesaRef = useRef(false);
    const bitcoinRef = useRef(false);
    const cashRef = useRef(true);
    const creditCardRef = useRef(false);

    transfersRef.current = watch('transfers', false);
    remesaRef.current = watch('remesa', false);
    bitcoinRef.current = watch('bitcoin', false);
    cashRef.current = watch('cash', true);
    creditCardRef.current = watch('creditCard', false);

    const _validateNumbers = (value) => {
        if (!value) return false;

        // Check if it's a range (contains dash)
        if (value.includes('-')) {
            const parts = value.split('-');
            if (parts.length !== 2) return false;

            const start = parseInt(parts[0].trim());
            const end = parseInt(parts[1].trim());

            return !isNaN(start) && !isNaN(end) && start > 0 && end > 0 && start <= end;
        }

        // Check if it's a single number
        const num = parseInt(value);
        return !isNaN(num) && num > 0;
    };

    const _validateNonRequiredNumbers = (value) => {
        if (value === '') {
            return true; // Optional field
        }

        // Check if it's a range (contains dash)
        if (value.includes('-')) {
            const parts = value.split('-');
            if (parts.length !== 2) return false;

            const start = parseInt(parts[0].trim());
            const end = parseInt(parts[1].trim());

            return !isNaN(start) && !isNaN(end) && start > 0 && end > 0 && start <= end;
        }

        // Check if it's a single number
        const num = parseInt(value);
        return !isNaN(num) && num > 0;
    };

    const _validateNonRequiredNumbersExamCost = (value) => {
        if (value === '') {
            return true; // Optional field
        }

        // Check if it's a range (contains dash)
        if (value.includes('-')) {
            const parts = value.split('-');
            if (parts.length !== 2) return false;

            const start = parseInt(parts[0].trim());
            const end = parseInt(parts[1].trim());

            return !isNaN(start) && !isNaN(end) && start >= 0 && end >= 0 && start <= end;
        }

        // Check if it's a single number
        const num = parseInt(value);
        return !isNaN(num) && num >= 0;
    };

    const _validatePhoneNumber = (value) => {
        if (!value) return false;
        // Phone number should be at least 7 digits (including country code)
        return value.length >= 7;
    };

    const _handleCostInputKeyDown = (e) => {
        // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
        if (
            [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
            (e.keyCode === 65 && e.ctrlKey === true) || // Ctrl+A
            (e.keyCode === 67 && e.ctrlKey === true) || // Ctrl+C
            (e.keyCode === 86 && e.ctrlKey === true) || // Ctrl+V
            (e.keyCode === 88 && e.ctrlKey === true) || // Ctrl+X
            (e.keyCode === 90 && e.ctrlKey === true) || // Ctrl+Z
            // Allow: numbers (0-9)
            (e.keyCode >= 48 && e.keyCode <= 57) ||
            // Allow: numpad numbers (0-9)
            (e.keyCode >= 96 && e.keyCode <= 105) ||
            // Allow: dash (-) for ranges
            e.keyCode === 189 ||
            e.keyCode === 109
        ) {
            return;
        }
        // Prevent all other keys
        e.preventDefault();
    };

    const _paymentMethodsValidations = () => {
        return (
            cashRef.current || creditCardRef.current || transfersRef.current || bitcoinRef.current || remesaRef.current
        );
    };

    // useEffect(() => {
    //     if (fields.length === 0) {
    //         append({ address: '' });
    //     }
    // }, [fields, append]);

    // useEffect(() => {
    //     if ((!defaultValues?.addresses || defaultValues.addresses.length === 0) && fields.length === 0) {
    //         append({ address: '' });
    //     }
    // }, [append]);

    // useEffect(() => {
    //     console.log('🏁 useEffect triggered with defaultValues:', defaultValues);

    //     const noDefaults = !defaultValues?.addresses || defaultValues.addresses.length === 0;
    //     console.log('🔍 noDefaults:', noDefaults);
    //     console.log('📦 current fields length:', fields.length);

    //     if (noDefaults && fields.length === 0) {
    //         console.log('🆕 No defaults and no fields → adding one empty address');
    //         append({ address: '' });
    //     }

    //     if (!noDefaults && fields.length !== defaultValues.addresses.length) {
    //         console.log('🔁 Defaults exist but fields mismatch → resetting all fields');
    //         console.log('🧹 Removing all current fields...');
    //         remove();

    //         console.log('➕ Appending addresses from defaults:', defaultValues.addresses);
    //         defaultValues.addresses.forEach((addr, idx) => {
    //             console.log(`➡️ Appending address[${idx}]:`, addr);
    //             append({ address: addr.address || '' });
    //         });
    //     }
    // }, [defaultValues]);

    useEffect(() => {
        if (!defaultValues) return;

        const defaultAddresses = defaultValues.addresses || [];
        const hasNoDefaults = defaultAddresses.length === 0;

        //  Step 1: prevent double insert — Only act if fields are empty
        if (hasNoDefaults && fields.length === 0) {
            append({ address: '' });
        }

        //  Step 2: if defaults exist and mismatch in count → replace all
        if (!hasNoDefaults && fields.length !== defaultAddresses.length) {
            remove();
            defaultAddresses.forEach((addr, i) => {
                append({ address: addr.address || '' });
            });
        }

        //  Step 3: do nothing if already matching → stable UI
    }, [defaultValues]);

    return (
        <form
            action="#"
            method="POST"
            onSubmit={handleSubmit((data) => {
                // The phone numbers already include country codes from react-phone-input-2
                // We can also send the country codes separately if needed
                const formDataWithCountryCodes = {
                    ...data,
                    patientContactPhone1CountryCode: phone1CountryCode,
                    patientContactPhone2CountryCode: phone2CountryCode
                };
                saveProfileForm(formDataWithCountryCodes);
            })}
        >
            <WhiteCardContainer>
                <FormFieldsContainer>
                    <ProfileFormHeader
                        title={'Bienvenido Doctor'}
                        initialText={'Ingresa la siguiente información para iniciar con tu perfil.'}
                        secondaryText={
                            'Una vez guardado este registro podrás acceder a la información adicional e iniciar el\n' +
                            '                            proceso de validación de perfil.'
                        }
                    />

                    <div className="grid grid-cols-6 gap-6">
                        <HalfRow>
                            <label htmlFor="patientContactPhone1" className="block text-sm font-medium text-dl-accent">
                                Teléfono Principal
                            </label>
                            <div className="relative mt-1 rounded-md shadow-sm">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <PhoneIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                                <Controller
                                    name="patientContactPhone1"
                                    control={control}
                                    rules={{
                                        required: true,
                                        validate: _validatePhoneNumber
                                    }}
                                    render={({ field: { onChange, value, name } }) => {
                                        const combinedValue =
                                            value && phone1CountryCode ? `${phone1CountryCode}${value}` : value || '';
                                        return (
                                            <PhoneInput
                                                country="sv"
                                                value={combinedValue}
                                                enableSearch
                                                onChange={(phone, country) => {
                                                    const phoneNumber = phone.replace(country.dialCode, '');
                                                    onChange(phoneNumber);
                                                    setPhone1CountryCode(country.dialCode);
                                                }}
                                                inputProps={{
                                                    name: name,
                                                    required: true,
                                                    autoFocus: false
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <p className="mt-2 text-sm text-gray-500" id="patientContactPhone1-description">
                                Requerido
                            </p>
                            {errors.patientContactPhone1 && (
                                <p className="mt-2 text-sm text-red-700">
                                    {errors.patientContactPhone1.type === 'required'
                                        ? 'Este campo es requerido'
                                        : 'Ingrese un número de teléfono válido'}
                                </p>
                            )}
                        </HalfRow>

                        <HalfRow>
                            <label htmlFor="patientContactPhone2" className="block text-sm font-medium text-dl-accent">
                                Teléfono Secundario
                            </label>
                            <div className="relative mt-1 rounded-md shadow-sm">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <PhoneIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                                <Controller
                                    name="patientContactPhone2"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (!value) return true; // Optional field
                                            return _validatePhoneNumber(value);
                                        }
                                    }}
                                    render={({ field: { onChange, value, name } }) => {
                                        // Combine phone number with country code for display
                                        const combinedValue =
                                            value && phone2CountryCode ? `${phone2CountryCode}${value}` : value || '';
                                        return (
                                            <PhoneInput
                                                country="sv"
                                                value={combinedValue}
                                                enableSearch
                                                onChange={(phone, country) => {
                                                    const phoneNumber = phone.replace(country.dialCode, '');
                                                    onChange(phoneNumber);
                                                    setPhone2CountryCode(country.dialCode);
                                                }}
                                                inputProps={{
                                                    name: name,
                                                    autoFocus: false
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            {errors.patientContactPhone2 && (
                                <p className="mt-2 text-sm text-red-700">Ingrese un número de teléfono válido</p>
                            )}
                        </HalfRow>

                        <HalfRow>
                            <FormInputField
                                identifier={'consultationCost'}
                                label={'Costo de consulta'}
                                iconComponent={
                                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                }
                                type={'text'}
                                placeholder={'Ej: 50 o 40-60'}
                                onKeyDown={_handleCostInputKeyDown}
                                registration={register('consultationCost', {
                                    required: true,
                                    min: 0.0,
                                    validate: _validateNumbers
                                })}
                                errorMessage={
                                    errors.consultationCost
                                        ? 'Este campo es requerido. Debe ser un número positivo o un rango válido (ej: 1-10).'
                                        : null
                                }
                            />
                        </HalfRow>

                        <HalfRow>
                            <FormInputField
                                identifier={'examCost'}
                                label={'Costo de examen'}
                                iconComponent={
                                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                }
                                type={'text'}
                                placeholder={'Ej: 25 o 20-30'}
                                onKeyDown={_handleCostInputKeyDown}
                                registration={register('examCost', {
                                    min: 0.0,
                                    validate: _validateNonRequiredNumbersExamCost
                                })}
                                errorMessage={
                                    errors.examCost ? 'Debe ser un número positivo o un rango válido (ej: 1-10).' : null
                                }
                            />
                        </HalfRow>
                        <HalfRow>
                            <FormInputField
                                identifier={'conademNumber'}
                                label={'Número de autorización CONADEM'}
                                type={'text'}
                                onKeyDown={_handleCostInputKeyDown}
                                registration={register('conademNumber')}
                                errorMessage={
                                    errors.conademNumber
                                        ? 'Debe ser un número positivo o un rango válido (ej: 1-10).'
                                        : null
                                }
                            />
                        </HalfRow>

                        <FormFieldset
                            legend={'Métodos de pago'}
                            errorMessage={
                                !cashRef.current &&
                                !creditCardRef.current &&
                                !transfersRef.current &&
                                !bitcoinRef.current &&
                                !remesaRef.current
                                    ? 'Debe elegir al menos una opción'
                                    : null
                            }
                        >
                            <FormCheckBox
                                identifier={'cash'}
                                checked={false}
                                label={'Efectivo'}
                                registration={register('cash', { validate: _paymentMethodsValidations })}
                            />
                            <FormCheckBox
                                identifier={'creditCard'}
                                checked={false}
                                label={'Tarjeta de Crédito'}
                                registration={register('creditCard', { validate: _paymentMethodsValidations })}
                            />
                            <FormCheckBox
                                identifier={'transfers'}
                                checked={false}
                                label={'Transferencias'}
                                registration={register('transfers', { validate: _paymentMethodsValidations })}
                            />
                            <FormCheckBox
                                identifier={'bitcoin'}
                                checked={false}
                                label={'Bitcoin'}
                                registration={register('bitcoin', { validate: _paymentMethodsValidations })}
                            />
                            <FormCheckBox
                                identifier={'remesa'}
                                checked={false}
                                label={'Remesa'}
                                registration={register('remesa', { validate: _paymentMethodsValidations })}
                            />
                        </FormFieldset>

                        <FullRow>
                            <FormTextArea
                                label={'Permiso para ejercer'}
                                identifier={'governmentPermitInfo'}
                                rows={3}
                                placeholder={
                                    'Ej. Número de junta de vigilancia de la profesión No.123, inscrito como doctor el 27 de julio de 2005'
                                }
                                helperText={'Información relevante sobre el permiso para ejercer como médico.'}
                                registration={register('governmentPermitInfo')}
                            />
                        </FullRow>

                        <FullRow>
                            <FormTextArea
                                label={'Información general de consulta.'}
                                identifier={'generalInfo'}
                                rows={3}
                                placeholder={
                                    'Ej. Mi consultorio es el número 3. Si viene por primera vez traiga una radiografía y un examen de sangre.'
                                }
                                helperText={'Información relevante para el paciente que le visita.'}
                                registration={register('generalInfo')}
                            />
                        </FullRow>

                        <FullRow>
                            <FormInputField
                                label={'Aseguradoras con las que trabaja'}
                                type={'text'}
                                identifier={'insurance'}
                                placeholder={'Ej. ACSA, Mapfre, Asesuiza, SISA'}
                                helperText={'Separados por coma. Si no trabaja con ninguna dejar en blanco.'}
                                registration={register('insurance')}
                            />
                        </FullRow>
                        <FullRow>
                            {fields.map((field, index) => (
                                <FullRow key={field.id}>
                                    <FormInputField
                                        label={`Dirección del consultorio ${index + 1}`}
                                        type="text"
                                        identifier={`addresses.${index}.address`}
                                        registration={register(`addresses.${index}.address`, { required: true })}
                                        errorMessage={errors?.addresses?.[index]?.address && 'Este campo es requerido'}
                                    />
                                </FullRow>
                            ))}

                            <div className="col-span-6 flex gap-4">
                                {fields.length < 3 ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (fields.length < 3) append({ address: '' });
                                        }}
                                        className="text-blue-600 underline text-sm"
                                    >
                                        Agregar otra dirección
                                    </button>
                                ) : (
                                    <span className="text-gray-400 text-sm">Máximo 3 direcciones permitidas</span>
                                )}

                                {fields.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => remove(fields.length - 1)}
                                        className="text-red-600 underline text-sm"
                                    >
                                        Eliminar última
                                    </button>
                                )}
                            </div>
                        </FullRow>

                        {/*  LAT LONG */}

                        <FullRow>
                            <FormInputField
                                identifier={'latitudeAndLongitude'}
                                label={'Latitud y longitud'}
                                iconComponent={<MapPinIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                type={'text'}
                                placeholder="Ej. 13.699629843071902, -89.22413229268238"
                                helperText={
                                    <>
                                        {' '}
                                        Si necesita ayuda, lea este{' '}
                                        <a
                                            target="_blank"
                                            rel="noopener nofollow noreferrer"
                                            className="text-dl-primary-500"
                                            href="https://support.google.com/maps/answer/18539?co=GENIE.Platform%3DDesktop&hl=es-MX#:~:text=C%C3%B3mo%20obtener%20las%20coordenadas%20de,decimal%20en%20la%20parte%20superior."
                                        >
                                            artículo sobre como sacar coordenadas de Google Maps.
                                        </a>{' '}
                                    </>
                                }
                                registration={register('latitudeAndLongitude')}
                            />
                        </FullRow>

                        {/* END  LAT LONG */}

                        {/* CITY */}

                        <FullRow>
                            <FormSelectDrop
                                label={'Ciudad'}
                                autoComplete={'city'}
                                identifier={'idCity'}
                                options={formattedCities}
                                defaultValue={defaultValues.idCity}
                                helperText={'Requerido'}
                                registration={register('idCity', { required: true })}
                                errorMessage={errors.idCity ? 'Este campo es requerido' : null}
                            />
                        </FullRow>
                        {/* END CITY */}

                        {/* SPECIALITY */}

                        <FullRow>
                            <FormSelectDrop
                                label={'Especialidad Principal'}
                                identifier={'idSpecialty'}
                                options={formattedSpecialities}
                                defaultValue={defaultValues.idSpecialty}
                                helperText={'Requerido'}
                                registration={register('idSpecialty', { required: true })}
                                onChange={handlePrimarySpecialtyChange}
                                errorMessage={errors.idSpecialty ? 'Este campo es requerido' : null}
                            />
                        </FullRow>
                        {/* END SPECIALITY */}
                    </div>
                </FormFieldsContainer>
                <FormFieldsFooter>
                    <FormButton label={isEditing ? 'Actualizar' : 'Guardar'} type={'submit'} />
                </FormFieldsFooter>
            </WhiteCardContainer>
            {showErrorModal && (
                <CustomRedAlert
                    open={true}
                    title={`Ocurrió un error.`}
                    bodyText={errorBody}
                    onClose={closeErrorMessage}
                />
            )}

            {showSuccessModal && (
                <CustomSuccessModal
                    title={isEditing ? 'Perfil actualizado con éxito' : 'Perfil inicial guardado con éxito'}
                    bodyText={successBody}
                    buttonText={'Actualizar Perfil'}
                    buttonAction={onCloseSuccessMessage}
                    open={showSuccessModal}
                    onClose={onCloseSuccessMessage}
                />
            )}
        </form>
    );
}
